import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Button,
  Checkbox,
  IconArrowRight,
  IconButton,
  IconMagnifyingGlass,
  IconPencil,
  IconPlus,
  IconTag,
  IconTrash,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableSortIndicator,
  Tooltip
} from '@vtex/shoreline'

import PageTitle from '~/components/PageTitle'
import { isReadOnly } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { BulkBar } from '../../components/advertiserBrand/BulkBar'
import { CatalogListCard } from '../../components/advertiserBrand/CatalogListCard'
import { NtbGate } from '../../components/advertiserBrand/NtbGate'
import { PageEmptyState } from '../../components/advertiserBrand/PageEmptyState'
import { PageHeader } from '../../components/advertiserBrand/PageHeader'
import { SearchFilter } from '../../components/advertiserBrand/SearchFilter'
import styles from '../../components/advertiserBrand/styles.module.css'
import { useConfirmDelete } from '../../components/advertiserBrand/useConfirmDelete'
import { usePushSearchParams } from '../../components/advertiserBrand/usePushSearchParams'
import { useRowSelection } from '../../components/advertiserBrand/useRowSelection'
import {
  useAdvertiserBrandMutations,
  useAdvertiserBrands
} from '../../hooks/useAdvertiserBrands'
import { useNtbMetricsGate } from '../../hooks/useNtbMetricsEnabled'
import { NewBrandModal, NewBrandModalState } from './elements/NewBrandModal'

// seleção · nome · contagem de produtos (· ações, só para quem pode editar)
const COLUMN_WIDTHS = ['2.5rem', 'minmax(0, 1fr)', '11rem']
const ACTIONS_COLUMN_WIDTH = '3.25rem'
const QUANTITY = 25

type SortableColumn = NonNullable<ListAdvertiserBrandsRequest['order_by']>

const getName = (brand: AdvertiserBrand) => brand.name

const AdvertiserBrandsPage: React.FC = () => {
  const { t } = useAPPTranslation()

  const queryParams = useQueryParams()
  const pushParams = usePushSearchParams()
  const page = Number(queryParams.get('page')) || 1
  const query = queryParams.get('query') || undefined
  const orderBy = (queryParams.get('order_by') || 'name') as SortableColumn
  const direction =
    queryParams.get('order_direction') === 'desc' ? 'desc' : ('asc' as const)

  // o mesmo gate que decide o corpo da página segura a chamada à API
  const { isEnabled: isNtbEnabled } = useNtbMetricsGate()

  const { data, isPending, error } = useAdvertiserBrands(
    {
      page,
      quantity: QUANTITY,
      query,
      order_by: orderBy,
      order_direction: direction
    },
    { enabled: isNtbEnabled }
  )
  const { deleteBrand } = useAdvertiserBrandMutations()

  const brands = useMemo(() => data?.data ?? [], [data?.data])
  const total = data?.total ?? 0

  const selection = useRowSelection(brands, getName)
  const confirmDelete = useConfirmDelete<string>()

  const [modal, setModal] = useState<NewBrandModalState>(null)

  /** Clicar na coluna já ordenada inverte a direção; em outra, começa em asc. */
  const handleSort = (column: SortableColumn) =>
    pushParams(params => {
      params.set('order_by', column)
      params.set(
        'order_direction',
        orderBy === column && direction === 'asc' ? 'desc' : 'asc'
      )
      params.set('page', '1')
    })

  const sortIndicator = (column: SortableColumn) =>
    orderBy === column ? direction : null

  const handleDelete = async () => {
    await deleteBrand.mutateAsync(confirmDelete.items)

    // excluir pelo kebab preserva a seleção, menos as marcas que acabaram de sair
    if (confirmDelete.fromSelection) selection.clear()
    else selection.deselect(confirmDelete.items)

    confirmDelete.close()
  }

  const countLabel = selection.selected.length
    ? t('rm:advertiserBrands.selected', { count: selection.selected.length })
    : t('rm:advertiserBrands.count', { count: total })

  const canManage = !isReadOnly()
  const columnWidths = canManage
    ? [...COLUMN_WIDTHS, ACTIONS_COLUMN_WIDTH]
    : COLUMN_WIDTHS

  const deleteAction = {
    id: 'delete',
    label: t('rm:advertiserBrands.actions.delete'),
    icon: <IconTrash />,
    critical: true,
    onClick: () =>
      confirmDelete.ask(selection.selected, { fromSelection: true })
  }

  const newBrandLabel = String(t('rm:advertiserBrands.cta.newBrand'))

  const newBrandButton = (
    <Button variant="primary" onClick={() => setModal({ mode: 'create' })}>
      <IconPlus />
      {t('rm:advertiserBrands.cta.newBrand')}
    </Button>
  )

  return (
    <div className={styles.page}>
      <PageTitle title={t('rm:advertiserBrands.pageTitle')} />

      <PageHeader
        title={t('rm:advertiserBrands.pageTitle')}
        actions={
          isNtbEnabled && (
            <>
              <SearchFilter
                placeholder={t('rm:advertiserBrands.search.placeholder')}
              />
              {canManage && (
                <Tooltip label={newBrandLabel}>
                  <IconButton
                    variant="secondary"
                    label={newBrandLabel}
                    onClick={() => setModal({ mode: 'create' })}
                  >
                    <IconPlus />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )
        }
      />

      <NtbGate>
        <CatalogListCard
          isPending={isPending}
          isEmpty={!brands.length}
          error={error}
          hasFilters={!!query}
          empty={
            <PageEmptyState
              icon={<IconTag width={28} height={28} />}
              title={t('rm:advertiserBrands.empty.title')}
              description={t('rm:advertiserBrands.empty.description')}
              action={canManage ? newBrandButton : undefined}
            />
          }
          emptyFiltered={
            <PageEmptyState
              icon={<IconMagnifyingGlass width={28} height={28} />}
              title={t('rm:advertiserBrands.emptyFiltered.title')}
              description={t('rm:advertiserBrands.emptyFiltered.description', {
                query
              })}
              action={
                <Button
                  variant="tertiary"
                  onClick={() =>
                    pushParams(params => {
                      params.delete('query')
                      params.set('page', '1')
                    })
                  }
                >
                  {t('rm:advertiserBrands.emptyFiltered.cta')}
                </Button>
              }
            />
          }
          countLabel={countLabel}
          page={page}
          pageSize={QUANTITY}
          total={total}
          onPageChange={nextPage =>
            pushParams(params => params.set('page', String(nextPage)))
          }
        >
          <Table columnWidths={columnWidths}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  <Checkbox
                    checked={selection.allSelected}
                    indeterminate={
                      !selection.allSelected && selection.someSelected
                    }
                    onChange={selection.toggleAll}
                    aria-label={t('rm:advertiserCatalog.selectAll')}
                  />
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  aria-sort={orderBy === 'name' ? `${direction}ending` : 'none'}
                  onClick={() => handleSort('name')}
                >
                  {t('rm:advertiserBrands.table.column.name')}
                  <TableSortIndicator sorted={sortIndicator('name')} />
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  aria-sort={
                    orderBy === 'products_count' ? `${direction}ending` : 'none'
                  }
                  onClick={() => handleSort('products_count')}
                >
                  {t('rm:advertiserBrands.table.column.productsCount')}
                  <TableSortIndicator
                    sorted={sortIndicator('products_count')}
                  />
                </TableHeaderCell>
                {canManage && <TableHeaderCell />}
              </TableRow>
            </TableHeader>

            <TableBody>
              {brands.map(brand => (
                <TableRow
                  key={brand.name}
                  selected={selection.isSelected(brand.name)}
                >
                  <TableCell>
                    <span onClickCapture={selection.registerShift}>
                      <Checkbox
                        checked={selection.isSelected(brand.name)}
                        onChange={() => selection.toggle(brand)}
                        aria-label={brand.name}
                      />
                    </span>
                  </TableCell>

                  <TableCell>
                    <span className={styles.brandText}>
                      <span className={styles.brandName}>{brand.name}</span>
                    </span>
                  </TableCell>

                  <TableCell>
                    <Link
                      className={styles.usageLink}
                      to={`/advertiser-catalog/products?brand=${encodeURIComponent(
                        brand.name
                      )}`}
                      title={t('rm:advertiserBrands.actions.viewProducts')}
                    >
                      {t('rm:advertiserBrands.table.productsLink', {
                        count: brand.products_count
                      })}
                      <span className={styles.usageArrow}>
                        <IconArrowRight width={14} height={14} />
                      </span>
                    </Link>
                  </TableCell>

                  {canManage && (
                    <TableCell align="end">
                      <Menu
                        label={t('rm:advertiserBrands.actions.menu', {
                          brand: brand.name
                        })}
                        type="actions"
                        iconOnly
                      >
                        <MenuItem
                          onClick={() => setModal({ mode: 'edit', brand })}
                        >
                          <IconPencil />
                          {t('rm:advertiserBrands.actions.edit')}
                        </MenuItem>
                        <MenuItem
                          critical
                          onClick={() => confirmDelete.ask([brand.name])}
                        >
                          <IconTrash />
                          {t('rm:advertiserBrands.actions.delete')}
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CatalogListCard>

        <BulkBar
          count={selection.selected.length}
          label={t('rm:advertiserBrands.selected', {
            count: selection.selected.length
          })}
          onClear={selection.clear}
          actions={canManage ? [deleteAction] : []}
        />

        <NewBrandModal
          state={modal}
          onClose={() => setModal(null)}
          onRenamed={previousName => selection.deselect([previousName])}
        />

        <confirmDelete.ConfirmationModal
          isOpen={confirmDelete.isOpen}
          title={t('rm:advertiserBrands.delete.confirm.title', {
            count: confirmDelete.items.length
          })}
          onClose={confirmDelete.close}
          confirmationButton={t('rm:advertiserBrands.delete.confirm.submit')}
          cancelButton={t('rm:button.Cancel')}
          submitLoading={deleteBrand.isPending}
          onSubmit={handleDelete}
        >
          {t('rm:advertiserBrands.delete.confirm.description', {
            count: confirmDelete.items.length,
            brand: confirmDelete.items[0]
          })}
        </confirmDelete.ConfirmationModal>
      </NtbGate>
    </div>
  )
}

export default AdvertiserBrandsPage
